import { FC, useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import TemplateCodeEditor from "./code-editor"
import FlatTemplateBuilder from "./flat-template-builder"

interface Props {
  template: string
  onChange: (template: string) => void
  dataType: "lead" | "subscription" | "pending_subscription"
  contentType?: string
}

const TemplateEditor: FC<Props> = ({
  template,
  onChange,
  dataType,
  contentType
}) => {
  const [showVariables, setShowVariables] = useState(false)
  const isFlat = contentType && contentType !== "application/json"

  const loadSampleTemplate = () => {
    let sampleTemplateString

    if (isFlat) {
      // Flat structure for URL-encoded and form-data (with @transform)
      sampleTemplateString =
        dataType === "lead"
          ? `{
  "first_name": "{{item.details.first_name}}",
  "last_name": "{{item.details.last_name}}",
  "email": "{{item.email}}",
  "phone": "{{item.phone}}",
  "loan_amount": {{item.details.loan_amount}},
  "credit_score": {{item.details.credit_score}},
  "loan_purpose_code": {{@transform(item.details.loan_purpose)}},
  "property_type_code": {{@transform(item.details.property_type)}},
  "residency_type_code": {{@transform(item.details.residency_type)}},
  "lo_nmls": "{{lo.nmls}}",
  "lo_email": "{{lo.email}}",
  "brand_name": "{{brand.title}}"
}`
          : `{
  "email": "{{item.email}}",
  "phone": "{{item.phone}}",
  "first_name": "{{item.first_name}}",
  "last_name": "{{item.last_name}}",
  "loan_purpose_code": {{@transform(item.quote_info.loan_purpose)}},
  "credit_score": {{item.quote_info.credit_score}},
  "property_type_code": {{@transform(item.quote_info.property_type)}},
  "lo_nmls": "{{lo.nmls}}",
  "lo_email": "{{lo.email}}"
}`
    } else {
      // Nested structure for JSON (with @transform)
      sampleTemplateString =
        dataType === "lead"
          ? `{
  "lead": {
    "first_name": "{{item.details.first_name}}",
    "last_name": "{{item.details.last_name}}",
    "email": "{{item.email}}",
    "phone": "{{item.phone}}",
    "loan_amount": {{item.details.loan_amount}},
    "credit_score": {{item.details.credit_score}},
    "loan_purpose_code": {{@transform(item.details.loan_purpose)}},
    "property_type_code": {{@transform(item.details.property_type)}},
    "residency_type_code": {{@transform(item.details.residency_type)}}
  },
  "loan_officer": {
    "name": "{{lo.full_name}}",
    "nmls": "{{lo.nmls}}",
    "email": "{{lo.email}}",
    "phone": "{{lo.phone}}"
  },
  "brand": {
    "name": "{{brand.title}}",
    "nmls": "{{brand.nmls}}"
  }
}`
          : `{
  "subscription": {
    "email": "{{item.email}}",
    "phone": "{{item.phone}}",
    "first_name": "{{item.first_name}}",
    "last_name": "{{item.last_name}}",
    "loan_purpose_code": {{@transform(item.quote_info.loan_purpose)}},
    "credit_score": {{item.quote_info.credit_score}},
    "property_type_code": {{@transform(item.quote_info.property_type)}}
  },
  "loan_officer": {
    "nmls": "{{lo.nmls}}",
    "email": "{{lo.email}}"
  }
}`
    }

    onChange(sampleTemplateString)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {isFlat ? "Data Template *" : "JSON Template *"}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {isFlat
              ? "Build flat key-value pairs using template variables (nested objects not supported for URL-encoded/form-data)"
              : "Build the structure of data sent to your CRM using template variables. Type {{ to see autocomplete."}
          </p>
        </div>
        <button
          type="button"
          onClick={loadSampleTemplate}
          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Load Sample
        </button>
      </div>

      {isFlat ? (
        <FlatTemplateBuilder
          template={template}
          onChange={onChange}
          dataType={dataType}
        />
      ) : (
        <TemplateCodeEditor
          value={template}
          onChange={onChange}
          dataType={dataType}
        />
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => setShowVariables(!showVariables)}
          className="w-full px-3 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-blue-100"
        >
          <span>Available Template Variables</span>
          {showVariables ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </button>

        {showVariables && (
          <div className="px-3 pb-3 pt-2 text-xs space-y-3">
            {/* Item/Record Variables */}
            <div>
              <div className="font-medium text-gray-800 mb-2">
                {dataType === "lead"
                  ? "Lead Data (item.*)"
                  : dataType === "subscription"
                  ? "Subscription Data (item.*)"
                  : "Pending Subscription Data (item.*)"}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{item.email}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{item.phone}}"}
                  </code>
                </div>

                {dataType === "lead" && (
                  <>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.first_name}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.last_name}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.loan_amount}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.credit_score}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.loan_purpose}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.loan_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.loan_term}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.list_price}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.down_payment}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.property_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.residency_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.realtor}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.location.city}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.location.state}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.location.zipcode}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.location.county}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.details.message}}"}
                      </code>
                    </div>
                  </>
                )}

                {(dataType === "subscription" ||
                  dataType === "pending_subscription") && (
                  <>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.first_name}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.last_name}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.loid}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.loan_purpose}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.credit_score}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.property_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.residency_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.list_price}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.loan_amount}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.loan_term}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.loan_type}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.location.city}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.location.state}}"}
                      </code>
                    </div>
                    <div>
                      <code className="bg-white px-1 py-0.5 rounded">
                        {"{{item.quote_info.location.zipcode}}"}
                      </code>
                    </div>
                    {dataType === "pending_subscription" && (
                      <div>
                        <code className="bg-white px-1 py-0.5 rounded">
                          {"{{item.trigger_rate}}"}
                        </code>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Loan Officer Variables */}
            <div className="border-t border-blue-200 pt-2">
              <div className="font-medium text-gray-800 mb-2">
                Loan Officer Data (lo.*)
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.full_name}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.nmls}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.email}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.phone}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.bank_nid}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{lo.bank_name}}"}
                  </code>
                </div>
              </div>
            </div>

            {/* Brand Variables */}
            <div className="border-t border-blue-200 pt-2">
              <div className="font-medium text-gray-800 mb-2">
                Brand Data (brand.*)
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{brand.title}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{brand.nmls}}"}
                  </code>
                </div>
                <div>
                  <code className="bg-white px-1 py-0.5 rounded">
                    {"{{brand.nid}}"}
                  </code>
                </div>
              </div>
            </div>

            {/* Usage Tips */}
            <div className="border-t border-blue-200 pt-2">
              <div className="font-medium text-gray-800 mb-2">Usage Tips</div>
              <div className="space-y-1 text-gray-700">
                <div>
                  • String values:{" "}
                  <code className="bg-white px-1 py-0.5 rounded">
                    "{`"{{item.email}}"`}"
                  </code>{" "}
                  (with quotes)
                </div>
                <div>
                  • Numeric values:{" "}
                  <code className="bg-white px-1 py-0.5 rounded">{`{{item.details.loan_amount}}`}</code>{" "}
                  (no quotes)
                </div>
                <div>
                  • Value transforms:{" "}
                  <code className="bg-white px-1 py-0.5 rounded">{`{{@transform(item.details.loan_purpose)}}`}</code>
                </div>
                <div>• Missing values become empty strings automatically</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplateEditor
